import React, { useState } from "react";
import { IconPlus, IconSearch, IconFilter, IconCalendar, IconUser, IconPin, IconPaperclip, IconX } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchNoticeboards, createNoticeboard, updateNoticeboard, HrNoticeboard } from "../../../apis/hrApi";
import toast from "react-hot-toast";

export default function Noticeboard() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    target: "All Employees",
    publisherName: "HR Department", // default or fetched from context
  });

  // Debounce search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch notices
  const { data, isLoading } = useQuery({
    queryKey: ["noticeboards", debouncedSearch],
    queryFn: () => fetchNoticeboards(1, 50, debouncedSearch),
  });

  const notices = data?.data?.noticeboards || [];

  // Create notice
  const createMutation = useMutation({
    mutationFn: createNoticeboard,
    onSuccess: () => {
      toast.success("Notice published successfully!");
      queryClient.invalidateQueries({ queryKey: ["noticeboards"] });
      setIsModalOpen(false);
      setFormData({
        title: "",
        content: "",
        target: "All Employees",
        publisherName: "HR Department",
      });
    },
    onError: () => {
      toast.error("Failed to publish notice");
    },
  });

  // Toggle pin
  const togglePinMutation = useMutation({
    mutationFn: ({ id, isPinned }: { id: string; isPinned: boolean }) =>
      updateNoticeboard(id, { isPinned }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["noticeboards"] });
    },
    onError: () => {
      toast.error("Failed to update notice status");
    },
  });

  const handlePublish = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      companyId: "63690d57-3f36-4076-9040-349fcc01a357", // MOCK OR FETCHED FROM AUTH CONTEXT
      title: formData.title,
      content: formData.content,
      target: formData.target,
      publisherName: formData.publisherName,
    });
  };

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300">
      
      {/* Top Header */}
      <div className="flex items-center justify-between mb-4 px-2">
        <div>
          <h1 className="text-2xl font-bold text-[#212121]">Company Bulletin & Noticeboard</h1>
          <p className="text-xs text-gray-500 mt-0.5">Broadcast announcements, policy directives, and alerts to branches</p>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-[#FBD037] text-[#212121] px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-semibold hover:bg-[#FACC15] transition shadow-sm"
          >
            <IconPlus size={16} /> Publish Notice
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 px-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <IconSearch size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search announcements..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl border border-gray-200 bg-white text-sm outline-none focus:border-gray-300 w-64 shadow-sm"
            />
          </div>
          <button className="border border-gray-200 bg-white text-gray-700 hover:border-[#FBD037] hover:bg-[#FBD037]/10 px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-semibold transition shadow-sm">
            <IconFilter size={16} /> Filter
          </button>
        </div>
      </div>

      {/* Grid of notices */}
      {isLoading ? (
        <div className="px-2 text-sm text-gray-500">Loading notices...</div>
      ) : notices.length === 0 ? (
        <div className="px-2 text-sm text-gray-500">No announcements found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {notices.map((item: HrNoticeboard) => (
            <div 
              key={item.id} 
              className={`bg-white rounded-2xl shadow-sm border p-5 transition flex flex-col justify-between hover:shadow-md hover:border-[#FBD037] ${
                item.isPinned ? "border-amber-200 bg-amber-50/20" : "border-gray-100"
              }`}
            >
              <div>
                {/* Pin indicator */}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[10px] bg-[#FBD037]/20 border border-[#FBD037]/40 px-2 py-0.5 rounded-full font-bold text-gray-700">
                    {item.target}
                  </span>
                  
                  <button 
                    onClick={() => togglePinMutation.mutate({ id: item.id, isPinned: !item.isPinned })}
                    className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border transition-colors ${
                      item.isPinned 
                        ? "text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100" 
                        : "text-gray-400 bg-gray-50 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    <IconPin size={12} className={item.isPinned ? "text-amber-500 fill-amber-500" : ""} /> 
                    {item.isPinned ? "Pinned" : "Pin"}
                  </button>
                </div>

                {/* Title */}
                <h3 className="font-bold text-gray-800 text-[15px] mb-2 hover:text-[#212121] transition">{item.title}</h3>
                
                {/* Content */}
                <p className="text-xs text-gray-500 leading-relaxed mb-4 whitespace-pre-wrap">{item.content}</p>
              </div>

              {/* Bottom details */}
              <div className="pt-3.5 border-t border-gray-100 flex flex-col gap-2">
                {item.attachmentUrl && (
                  <div className="flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50/50 hover:bg-blue-50 border border-blue-200/50 rounded-lg p-2 cursor-pointer transition">
                    <IconPaperclip size={13} />
                    <span className="font-semibold truncate">{item.attachmentUrl}</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center text-[10px] text-gray-400">
                  <span className="flex items-center gap-1"><IconUser size={12} /> {item.publisherName}</span>
                  <span className="flex items-center gap-1"><IconCalendar size={12} /> {new Date(item.publishedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Publish Notice Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#212121]">Publish Notice</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full">
                <IconX size={20} />
              </button>
            </div>

            <form onSubmit={handlePublish} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Title</label>
                <input 
                  type="text" 
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FBD037]" 
                  placeholder="e.g. Upcoming Holiday - Eid-ul-Fitr"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Content</label>
                <textarea 
                  required
                  rows={4}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FBD037] resize-none" 
                  placeholder="Type the announcement details..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Target Audience</label>
                  <select 
                    value={formData.target}
                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FBD037]"
                  >
                    <option value="All Employees">All Employees</option>
                    <option value="All Branches">All Branches</option>
                    <option value="Tech & Support">Tech & Support</option>
                    <option value="HR Department">HR Department</option>
                    <option value="Management">Management</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Publisher</label>
                  <input 
                    type="text" 
                    required
                    value={formData.publisherName}
                    onChange={(e) => setFormData({ ...formData, publisherName: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FBD037]" 
                    placeholder="e.g. HR Department"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={createMutation.isPending}
                  className="bg-[#FBD037] text-[#212121] px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-semibold hover:bg-[#FACC15] transition shadow-sm disabled:opacity-50"
                >
                  {createMutation.isPending ? "Publishing..." : "Publish Now"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
